import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { useModelLoader } from '../../hooks/useModelLoader';
import { useCanvasContext } from '../../contexts/CanvasContext';
import { useMicrophone } from '../../contexts/MicrophoneContext';
import { useActiveScreen, usePreloading, useSound, PHONE_AUDIO_VOLUME } from '../../contexts/SiteContext';
import { useGPUTier } from '../../hooks/useGPUTier';
import { useOptimizedFrame, useConditionalFrame } from '../../hooks/useOptimizedFrame';
import AnimatedLogos from '../animations/AnimatedLogos';
import SoundWaves from '../animations/SoundWaves';
import CameraAlignedLabels from '../ui/CameraAlignedLabels';
import TvScene from './TvScene';
import NewsScene from './NewsScene';
import PhoneCable from './PhoneCable';
import sceneConfig, { createOptimizedSceneConfig, applySceneOffset, clearResponsiveCaches } from '../../config/sceneConfig';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { logger } from '../../utils/logger';
import { SOUND_OFF_PLAYBACK_TIMEOUT_MS, shouldApplySoundOffLimit } from '../../constants/audio';
import { isPhoneboxInteractiveElement, setCanvasPointer } from '../../utils/interactive3DHelpers';

// Canonical phonebox 3D scene used by the app
const Phonebox3DScene = React.memo(({ onSceneClick, onAnimationComplete, onAudioEnd, offsetPosition }) => {
  const { gltf, isLoading, error } = useModelLoader('/models/phonebox.glb', 'phonebox-scene');
  const { isPreloading } = usePreloading();
  const { performanceSettings } = useGPUTier();
  const { isSoundOn } = useSound();
  
  const { 
    registerSceneObject, 
    getSceneObject, 
    phoneboxState,
    setPhonePlaying,
    setAudioContext,
    setAudioData,
    setAnimationState,
    setIsInHoldPosition,
    setModelLoaded,
    readyToPosition,
    registerPhoneboxAudio
  } = useCanvasContext();
  
  const sceneRef = useRef();
  const mixerRef = useRef();
  const audioRef = useRef();
  const audioTimeoutRef = useRef();
  const gainNodeRef = useRef(null);
  const soundOffTimeoutRef = useRef(null); // Timeout for sound-off playback limit
  const isSoundOnRef = useRef(isSoundOn); // Track current sound state
  const onAudioEndRef = useRef(onAudioEnd); // Track onAudioEnd callback to avoid effect re-runs
  const isPlayingHangupAudioRef = useRef(false); // Track if we're playing hangup audio (to prevent event conflicts)
  const hangupAnimationCompletedRef = useRef(false); // Track if hangup animation completion has been handled (prevents repeated logs)
  const [sceneObject, setSceneObject] = useState(null);
  const [animations, setAnimations] = useState([]);
  const [currentAnimation, setCurrentAnimation] = useState(null);
  const [cameraAnimation, setCameraAnimation] = useState(null);
  const [isModelProcessed, setIsModelProcessed] = useState(false);
  const materialsInitializedRef = useRef(false);
  
  // Animation state
  const [animationProgress, setAnimationProgress] = useState(0);
  const hasLoggedAnimationEffectRef = useRef(false);

  // Reset animation progress when component mounts (canvas remount)
  useEffect(() => {
    setAnimationProgress(0);
    hasLoggedAnimationEffectRef.current = false; // Reset logging flag
    hangupAnimationCompletedRef.current = false; // Reset completion flag
    logger.scene('CombinedScene mounted - reset animation progress');
  }, []);
  
  // Camera animation state - removed as it's now handled by CombinedScene

  // Initialize Web Audio API for real-time analysis
  useEffect(() => {
    const initAudioContext = async () => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        
        setAudioContext(audioContext, analyser);
        logger.audio('Audio context initialized for real-time analysis');
      } catch (error) {
        console.warn('Could not initialize audio context:', error);
      }
    };

    initAudioContext();
  }, [setAudioContext]);


  // Handle user interaction to resume audio context (required for autoplay policies)
  useEffect(() => {
    const handleUserInteraction = async () => {
      if (phoneboxState.audioContext && phoneboxState.audioContext.state === 'suspended') {
        try {
          await phoneboxState.audioContext.resume();
          logger.audio('Audio context resumed after user interaction');
        } catch (error) {
          console.warn('Could not resume audio context:', error);
        }
      }
    };

    // Add event listeners for user interaction
    const events = ['click', 'touchstart', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { once: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };
  }, [phoneboxState.audioContext]);


  // Load audio file when component mounts
  useEffect(() => {
    // Prevent duplicate audio loading
    if (audioRef.current) {
      return;
    }

    const audio = new Audio('/audio/phonebox.mp3');
    audio.preload = 'auto';
    audio.volume = PHONE_AUDIO_VOLUME;
    audioRef.current = audio;
    
    // Register the audio ref in CanvasContext so QuestionSequence can reuse it for hangup.mp3
    if (registerPhoneboxAudio) {
      registerPhoneboxAudio(audio);
    }
    
    // Connect audio to Web Audio API for analysis
    const connectAudioToAnalyser = async () => {
      if (phoneboxState.audioContext && phoneboxState.analyser && !audioRef.current.connectedToAnalyser) {
        try {
          // Ensure audio context is resumed before connecting
          if (phoneboxState.audioContext.state === 'suspended') {
            await phoneboxState.audioContext.resume();
            logger.audio('Audio context resumed before connecting to analyser');
          }
          
          const source = phoneboxState.audioContext.createMediaElementSource(audio);
          
          // Create gain node to control volume without affecting analyser
          const gainNode = phoneboxState.audioContext.createGain();
          gainNodeRef.current = gainNode;
          
          // Split the source: connect to analyser for analysis (always full signal)
          source.connect(phoneboxState.analyser);
          
          // Also connect source to gain node, then to destination (gain controls user volume)
          source.connect(gainNode);
          gainNode.connect(phoneboxState.audioContext.destination);
          
          // Set initial gain based on sound state
          gainNode.gain.value = isSoundOn ? 1 : 0;
          
          // Mark as connected to prevent multiple connections
          audioRef.current.connectedToAnalyser = true;
          
          logger.audio('Audio connected to analyser for real-time visualization');
        } catch (error) {
          console.warn('Could not connect audio to analyser:', error);
        }
      }
    };

    // Add event listeners
    // Define handleAudioEnd first so it's available when handleAudioStart references it
    const handleAudioEnd = () => {
      setPhonePlaying(false);
      logger.audio('Phone audio ended - checking if phone is still off-hook');
      
      // Clear the fallback timeout
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
      
      // Clear the sound-off timeout if it exists
      if (soundOffTimeoutRef.current) {
        clearTimeout(soundOffTimeoutRef.current);
        soundOffTimeoutRef.current = null;
      }
      
      // IMPORTANT: Don't dispatch phoneAudioEnded event if we're playing hangup audio
      // This prevents restarting the question sequence when hangup audio ends
      if (isPlayingHangupAudioRef.current) {
        logger.audio('Hangup audio ended - skipping phoneAudioEnded event dispatch');
        isPlayingHangupAudioRef.current = false; // Reset flag
        hangupAnimationCompletedRef.current = false; // Reset completion flag so animation can complete normally next time
        return; // Exit early - don't dispatch event or call callback
      }
      
      // Call the onAudioEnd callback if provided (use ref to get latest version)
      if (onAudioEndRef.current) {
        onAudioEndRef.current();
      }
      
      // Only dispatch phoneAudioEnded event if phone is still off-hook
      // This prevents the event from firing after the user has hung up
      setTimeout(() => {
        // Check if phone is still off-hook before dispatching event
        if (phoneboxState.isOffHook) {
          logger.audio('Phone still off-hook - dispatching phoneAudioEnded event');
          window.dispatchEvent(new CustomEvent('phoneAudioEnded'));
        } else {
          logger.audio('Phone has been hung up - not dispatching phoneAudioEnded event');
        }
      }, 1000);
    };

    const handleAudioStart = () => {
      // Check if this is hangup audio by checking the src
      const isHangupAudio = audioRef.current && audioRef.current.src.includes('hangup.mp3');
      
      if (isHangupAudio) {
        // Mark that we're playing hangup audio to prevent event conflicts
        isPlayingHangupAudioRef.current = true;
        logger.audio('Hangup audio started - skipping phonebox audio handlers');
        // Still set phone playing state for UI consistency
        setPhonePlaying(true);
        // Don't set up timeouts or dispatch events for hangup audio
        return;
      }
      
      setPhonePlaying(true);
      connectAudioToAnalyser();
      
      // If sound is off, limit playback to the configured timeout
      if (!isSoundOnRef.current && shouldApplySoundOffLimit(audioRef.current, SOUND_OFF_PLAYBACK_TIMEOUT_MS, logger.audio)) {
        logger.audio(`Sound is off - limiting phonebox intro audio playback to ${SOUND_OFF_PLAYBACK_TIMEOUT_MS / 1000} seconds`);
        // Clear any existing timeout
        if (soundOffTimeoutRef.current) {
          clearTimeout(soundOffTimeoutRef.current);
        }
        // Set timeout to manually end audio after the configured limit
        soundOffTimeoutRef.current = setTimeout(() => {
          const audio = audioRef.current;
          // Check if audio is still playing and sound is still off
          if (audio && !audio.paused && audio.readyState >= 2 && !isSoundOnRef.current) {
            logger.audio(`${SOUND_OFF_PLAYBACK_TIMEOUT_MS / 1000}-second limit reached (sound off) - ending phonebox intro audio playback`);
            // Pause the audio
            audio.pause();
            // Manually trigger the ended handler
            handleAudioEnd();
          }
          soundOffTimeoutRef.current = null;
        }, SOUND_OFF_PLAYBACK_TIMEOUT_MS);
      }
      
      // Set a fallback timeout in case audio doesn't end properly
      audioTimeoutRef.current = setTimeout(() => {
        logger.audio('Audio timeout reached - checking if phone is still off-hook');
        // Only dispatch event if phone is still off-hook and not playing hangup audio
        if (phoneboxState.isOffHook && !isPlayingHangupAudioRef.current) {
          logger.debug('Phone still off-hook - forcing transition via phoneAudioEnded event');
          window.dispatchEvent(new CustomEvent('phoneAudioEnded'));
        } else {
          logger.debug('Phone has been hung up or playing hangup audio - not forcing transition');
        }
      }, 20000); // 20 second fallback
    };

    audio.addEventListener('play', handleAudioStart);
    audio.addEventListener('ended', handleAudioEnd);
    
    logger.audio('Phone audio connected to analyser');
    
    return () => {
      // Only clean up audio if it hasn't been initialized yet (component unmounting before audio loaded)
      // If audio is already loaded and connected, don't clean it up - let it continue playing
      // This prevents audio from stopping when component re-renders due to prop changes
      if (audioRef.current && !audioRef.current.connectedToAnalyser) {
        // Audio was created but not yet connected - safe to clean up completely
        audioRef.current.removeEventListener('play', handleAudioStart);
        audioRef.current.removeEventListener('ended', handleAudioEnd);
        audioRef.current.pause();
        audioRef.current = null;
      }
      // If audio is connected, don't remove listeners or pause - let it continue playing
      // The event listeners will persist and continue to work
      
      // Always clear timeouts on cleanup to prevent stale timeouts
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
      
      if (soundOffTimeoutRef.current) {
        clearTimeout(soundOffTimeoutRef.current);
        soundOffTimeoutRef.current = null;
      }
    };
  }, [phoneboxState.audioContext, phoneboxState.analyser, phoneboxState.isOffHook, setPhonePlaying, isSoundOn, registerPhoneboxAudio]);

  // Update onAudioEnd ref when it changes (without triggering audio effect re-run)
  useEffect(() => {
    onAudioEndRef.current = onAudioEnd;
  }, [onAudioEnd]);

  // React to global sound toggle - update gain node instead of audio element volume
  useEffect(() => {
    isSoundOnRef.current = isSoundOn; // Update ref with current sound state
    
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = isSoundOn ? 1 : 0;
    }
    // Also update audio element volume as fallback (though it doesn't affect Web Audio API)
    if (audioRef.current) {
      audioRef.current.muted = false; // Keep unmuted so analyser can analyze audio
      audioRef.current.volume = PHONE_AUDIO_VOLUME; // Keep at normal volume for Web Audio API
    }
    
    // If sound is turned on while audio is playing, clear the sound-off timeout
    if (isSoundOn && soundOffTimeoutRef.current) {
      clearTimeout(soundOffTimeoutRef.current);
      soundOffTimeoutRef.current = null;
      logger.audio('Sound turned on - cleared sound-off timeout for phonebox intro, allowing full playback');
    }
  }, [isSoundOn]);

  // Camera setup is now handled by PersistentCanvas to avoid interference with rotation

  // Handle two-part animation: pickup and hangup
  useEffect(() => {
    // Only log once per effect run to prevent duplicates
    if (!hasLoggedAnimationEffectRef.current) {
      logger.animation('Animation effect triggered:', {
        isOffHook: phoneboxState.isOffHook,
        animationState: phoneboxState.animationState,
        shouldStart: phoneboxState.isOffHook && phoneboxState.animationState === 'idle'
      });
      hasLoggedAnimationEffectRef.current = true;
    }
    
    if (phoneboxState.isOffHook && phoneboxState.animationState === 'idle') {
      // Start pickup animation (first half)
      logger.animation('Starting pickup animation (first half)');
      setAnimationState('pickup');
      setAnimationProgress(0);
      
      // Play phone pickup audio
      if (audioRef.current) {
        const playAudio = async () => {
          try {
            // Ensure audio context is resumed before playing
            if (phoneboxState.audioContext && phoneboxState.audioContext.state === 'suspended') {
              await phoneboxState.audioContext.resume();
              logger.audio('Audio context resumed before playing');
            }
            
            // Reset audio src to phonebox.mp3 if it's not already set (might be hangup.mp3 from previous session)
            const currentSrc = audioRef.current.src || '';
            const isPhoneboxAudio = currentSrc.includes('phonebox.mp3');
            
            if (!isPhoneboxAudio) {
              logger.audio('Resetting audio src to phonebox.mp3', {
                previousSrc: currentSrc
              });
              
              // Stop any current playback
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
              
              // Reset src to phonebox.mp3
              audioRef.current.src = '/audio/phonebox.mp3';
              
              // Call load() to ensure the new src is loaded
              audioRef.current.load();
              
              logger.audio('Audio src reset to phonebox.mp3 successfully');
            }
            
            audioRef.current.currentTime = 0; // Reset to beginning
            audioRef.current.muted = false; // Keep unmuted so analyser can analyze audio
            audioRef.current.volume = PHONE_AUDIO_VOLUME; // Keep at normal volume for Web Audio API
            // Volume is controlled by gainNode, not audio element volume
            await audioRef.current.play();
            logger.audio('Playing phone pickup audio');
          } catch (error) {
            console.warn('Could not play phone audio:', error);
            // Try to resume audio context and retry once
            if (phoneboxState.audioContext && phoneboxState.audioContext.state === 'suspended') {
              try {
                await phoneboxState.audioContext.resume();
                audioRef.current.muted = false; // Keep unmuted so analyser can analyze audio
                audioRef.current.volume = PHONE_AUDIO_VOLUME; // Keep at normal volume for Web Audio API
                // Volume is controlled by gainNode, not audio element volume
                await audioRef.current.play();
                logger.audio('Playing phone pickup audio (retry successful)');
              } catch (retryError) {
                console.warn('Could not play phone audio after retry:', retryError);
              }
            }
          }
        };
        
        playAudio();
      }
      
      // Camera positioning is handled by the main camera system in CombinedScene
      
      // Play all available animations from the beginning
      if (animations.length > 0 && mixerRef.current) {
        logger.animation('=== STARTING PICKUP ANIMATION ===');
        logger.animation('Number of animations found:', animations.length);
        
        // First, ensure all animations are stopped and reset to beginning
        try {
          mixerRef.current.stopAllAction();
          // Reset all animations to beginning for fresh start
          animations.forEach((clip) => {
            const action = mixerRef.current.clipAction(clip);
            action.reset();
            action.time = 0;
          });
          logger.animation('Reset all animations to beginning for fresh pickup');
        } catch (error) {
          console.warn('Error stopping and resetting actions:', error);
        }
        
        animations.forEach((clip, index) => {
          logger.animation(`Playing animation ${index}: ${clip.name} (${clip.duration}s)`);
          try {
            const action = mixerRef.current.clipAction(clip);
            action.reset();
            action.time = 0; // Start from beginning
            action.setLoop(THREE.LoopOnce); // Play only once, don't loop
            action.clampWhenFinished = true; // Stay at final position when done
            action.play();
            
            // Store the first animation as current for progress tracking
            if (index === 0) {
              setCurrentAnimation(action);
              logger.animation('Set as current animation:', clip.name);
            }
          } catch (error) {
            console.warn(`Error playing animation ${index} (${clip.name}):`, error);
          }
          
          // Check if this is a camera animation (outside try-catch since action might not be defined)
          if (clip.name.toLowerCase().includes('camera') || 
              clip.tracks.some(track => track.name.includes('camera'))) {
            logger.animation('Found camera animation:', clip.name);
            try {
              const action = mixerRef.current.clipAction(clip);
              setCameraAnimation(action);
            } catch (error) {
              console.warn('Error setting camera animation:', error);
            }
          }
        });
        logger.animation('===============================');
      } else {
        logger.animation('No animations found or mixer not ready');
        logger.animation('Animations array length:', animations.length);
        logger.animation('Mixer exists:', !!mixerRef.current);
      }
    } else if (!phoneboxState.isOffHook && phoneboxState.animationState === 'pickup') {
      // Check if we're still in pickup phase (before hold position)
      if (animationProgress < 0.5) {
        // User hung up before reaching hold position - reverse animation back to beginning
        logger.animation('User hung up before pickup complete - reversing animation to beginning');
        
        // Stop audio immediately
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          logger.audio('Stopped phone audio - pickup reversed');
        }
        setPhonePlaying(false);
        setIsInHoldPosition(false);
        
        // Set animation state to reverse
        setAnimationState('reverse');
        
        // Simple reverse: just set time scale to -1 for all animations
        if (animations.length > 0 && mixerRef.current) {
          logger.animation('=== REVERSING PICKUP ANIMATION ===');
          
          animations.forEach((clip, index) => {
            const action = mixerRef.current.clipAction(clip);
            
            // Safety check: ensure we have a valid action object
            if (!action || typeof action !== 'object') {
              console.warn(`Invalid action for animation ${index}: ${clip.name}`, action);
              return;
            }
            
            // Ensure animation is playing before reversing
            action.paused = false;
            action.enabled = true;
            action.setLoop(THREE.LoopOnce);
            action.clampWhenFinished = false; // Don't clamp when reversing
            action.setEffectiveTimeScale(-1); // Play in reverse
            action.play(); // Make sure it's playing
            
            logger.animation(`Reversing animation ${index}: ${clip.name} from time ${action.time}s`);
            
            // Store the first animation as current for progress tracking
            if (index === 0) {
              setCurrentAnimation(action);
              logger.animation('Set as current animation for reverse:', clip.name);
            }
          });
          logger.animation('==================================');
        }
      } else {
        // User hung up after reaching hold position - continue with normal hangup
        logger.animation('Resuming hangup animation (second half) from paused state');
        setAnimationState('hangup');
        
        // Resume animations from the paused state (50% mark)
        if (animations.length > 0 && mixerRef.current) {
          logger.animation('=== RESUMING HANGUP ANIMATION ===');
          
          animations.forEach((clip, index) => {
            const action = mixerRef.current.clipAction(clip);
            const halfDuration = clip.duration / 2;
            const resumeTime = halfDuration + 3.0; // Resume from 1 second after halfway
            
            // Check current animation time before resuming
            
            // Resume from where it was paused (50% + 3 seconds)
            action.time = resumeTime;
            action.paused = false; // Unpause the animation
            action.setLoop(THREE.LoopOnce); // Play only once, don't loop
            action.clampWhenFinished = true; // Stay at final position when done
            action.play();
            
            
            // Store the first animation as current for progress tracking
            if (index === 0) {
              setCurrentAnimation(action);
              // Update progress to reflect the resume point
              const resumeProgress = resumeTime / clip.duration;
              setAnimationProgress(resumeProgress);
            }
          });
          logger.animation('==================================');
        }
      }
    }
  }, [phoneboxState.isOffHook, phoneboxState.animationState, phoneboxState.audioContext, animations, currentAnimation, cameraAnimation, animationProgress, setAnimationState, setIsInHoldPosition, setPhonePlaying, isSoundOn]);

  // Real-time audio analysis and visualization - optimized for performance
  useOptimizedFrame(() => {
    // Update audio data for visualization
    if (phoneboxState.analyser && phoneboxState.isPlaying) {
      const bufferLength = phoneboxState.analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      phoneboxState.analyser.getByteFrequencyData(dataArray);
      
      // Calculate average amplitude for sound wave intensity
      const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
      setAudioData({ dataArray, average, bufferLength });
    }

    if (mixerRef.current && (phoneboxState.animationState === 'pickup' || phoneboxState.animationState === 'hangup' || phoneboxState.animationState === 'reverse')) {
      // Use performance-optimized frame rate for animations at 1.5x speed
      const frameTime = 1000 / performanceSettings.maxFPS; // Cap at 60fps for animations
      mixerRef.current.update((frameTime / 1000) * 1.5); // Run animations at 1.5x speed
      
      // Update progress based on animation time
      if (currentAnimation && currentAnimation.getClip()) {
        const clip = currentAnimation.getClip();
        const progress = currentAnimation.time / clip.duration;
        setAnimationProgress(Math.min(Math.max(progress, 0), 1)); // Clamp between 0 and 1
        
        // Check if pickup animation should pause at exactly halfway point (50%)
        if (phoneboxState.animationState === 'pickup' && progress >= 0.5 && !phoneboxState.isInHoldPosition) {
          const clip = currentAnimation.getClip();
          const halfDuration = clip.duration / 2;
          const pauseProgress = halfDuration / clip.duration;
          
          if (progress >= pauseProgress) {
            logger.animation('Pickup animation completed, pausing at halfway point');
            // Set hold position state to true when animation reaches pause point
            setIsInHoldPosition(true);
            // Pause the animation at the pause point
            if (currentAnimation) {
              currentAnimation.paused = true;
              currentAnimation.time = halfDuration;
              // Update progress to reflect the pause point
              setAnimationProgress(pauseProgress);
            }
            // Pause all animations at pause point
            if (mixerRef.current) {
              animations.forEach((clip) => {
                const action = mixerRef.current.clipAction(clip);
                const pauseTime = (clip.duration / 2) + 3.0; // 1 second after halfway for each animation
                action.paused = true;
                action.time = pauseTime;
              });
            }
          }
        }
        
        // Check if reverse animation is complete (back to beginning)
        if (phoneboxState.animationState === 'reverse' && currentAnimation && currentAnimation.time <= 0) {
          logger.animation('Reverse animation completed - phone back to beginning');
          
          // Stop and reset all animations
          if (mixerRef.current) {
            mixerRef.current.stopAllAction();
          }
          
          // Reset all animations to beginning
          animations.forEach((clip) => {
            const action = mixerRef.current.clipAction(clip);
            action.reset();
            action.time = 0;
            action.setEffectiveTimeScale(1); // Reset to normal speed
            action.paused = false; // Ensure not paused
          });
          
          // Reset state
          setAnimationState('idle');
          setAnimationProgress(0);
          setCurrentAnimation(null);
          
          logger.animation('Animation system reset - ready for next interaction');
        }
        
        // Reset completion flag when animation state changes away from 'hangup'
        if (phoneboxState.animationState !== 'hangup' && hangupAnimationCompletedRef.current) {
          hangupAnimationCompletedRef.current = false;
        }
        
        // Check if hangup animation is complete (second half)
        if (phoneboxState.animationState === 'hangup' && progress >= 1) {
          // Only handle completion once - prevent repeated logs/processing
          if (hangupAnimationCompletedRef.current) {
            return; // Already handled - exit early
          }
          
          logger.animation('Hangup animation (second half) completed');
          
          // CRITICAL: Check if hangup audio is playing - don't stop it!
          const isHangupAudioPlaying = audioRef.current && 
                                       audioRef.current.src && 
                                       audioRef.current.src.includes('hangup.mp3') &&
                                       !audioRef.current.paused &&
                                       !audioRef.current.ended;
          
          if (isHangupAudioPlaying) {
            logger.animation('Hangup animation completed but hangup audio is still playing - skipping all cleanup to allow audio to finish');
            // Mark as completed so we don't log again - wait for audio onended event
            hangupAnimationCompletedRef.current = true;
            // Don't stop audio, don't reset state, don't reset animations - let hangup audio finish playing
            // The hangup audio's onended handler will handle cleanup
            return; // Exit early - don't do any cleanup
          }
          
          // Mark as completed for non-hangup-audio case too
          hangupAnimationCompletedRef.current = true;
          
          // Reset animation state to idle
          setAnimationState('idle');
          setIsInHoldPosition(false);
          setAnimationProgress(0); // Reset to 0 to indicate phone is fully back on hook
          
          // Stop audio and reset phone state (only if not hangup audio)
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            logger.audio('Stopped phone audio');
          }
          setPhonePlaying(false);
          
          // Camera positioning is handled by the main camera system in CombinedScene
          
          // Stop animations but keep them at final position (don't reset)
          if (mixerRef.current) {
            try {
              mixerRef.current.stopAllAction();
              logger.animation('Stopped all animation actions at final position');
            } catch (error) {
              console.warn('Error stopping actions:', error);
            }
          }
          
          // Stop individual animation references but keep at final position
          if (currentAnimation) {
            try {
              currentAnimation.stop();
              // Don't reset - keep at final position
              logger.animation('Stopped current animation at final position');
            } catch (error) {
              console.warn('Error stopping current animation:', error);
            }
            setCurrentAnimation(null);
          }
          
          if (cameraAnimation) {
            try {
              cameraAnimation.stop();
              // Don't reset - keep at final position
              logger.animation('Stopped camera animation at final position');
            } catch (error) {
              console.warn('Error stopping camera animation:', error);
            }
            setCameraAnimation(null);
          }
          
          // Reset all animations to beginning position (phone fully back on hook)
          if (animations.length > 0 && mixerRef.current) {
            animations.forEach((clip) => {
              const action = mixerRef.current.clipAction(clip);
              action.reset();
              action.time = 0; // Reset to beginning position
              action.stop();
            });
            logger.animation('Reset all animations to beginning - phone fully back on hook');
          }
          
          // IMPORTANT: Don't call onAnimationComplete if hangup audio is playing or loading
          // This prevents the animation from hanging up the phone before hangup audio finishes
          const isHangupAudioActive = audioRef.current && 
                                      audioRef.current.src && 
                                      audioRef.current.src.includes('hangup.mp3') &&
                                      (!audioRef.current.paused || audioRef.current.readyState < 2);
          
          // Also check if we're in question mode - if so, don't auto-hangup (let question sequence handle it)
          const isInQuestionMode = phoneboxState.isInQuestionMode;
          
          if (isHangupAudioActive || isInQuestionMode) {
            if (isHangupAudioActive) {
              logger.animation('Hangup animation completed but hangup audio is active - delaying onAnimationComplete');
            } else {
              logger.animation('Hangup animation completed but in question mode - delaying onAnimationComplete');
            }
            // Don't call onAnimationComplete - let hangup audio/question sequence handle hangup
            // The hangup audio's onended handler will call processHangup which will hang up the phone
            return;
          }
          
          if (onAnimationComplete) {
            onAnimationComplete();
          }
        }
      }
    }
  });

  // Model loading and processing
  useEffect(() => {
    if (gltf && gltf.scene && !isModelProcessed) {
      // Check if we already have processed scene in the global context
      const existingSceneObject = getSceneObject('phonebox-scene');
      
      if (existingSceneObject) {
        setSceneObject(existingSceneObject);
        
        // Get animations from cached scene
        if (gltf.animations && gltf.animations.length > 0) {
          setAnimations(gltf.animations);
        }
        setIsModelProcessed(true);
        return;
      }

      // Process the model for the first time
      
      // Adjust scale and position for the new model
      // Get bounding box to understand the model size
      new THREE.Box3().setFromObject(gltf.scene);
      
      // Use configuration for phonebox positioning
      const phoneboxConfig = sceneConfig.modelPositions.phonebox;
      gltf.scene.scale.setScalar(phoneboxConfig.scale);
      
      // Use loading position during preload, final position with offset otherwise
      // All models use the same preload position for consistent viewing
      // During preload, ignore offsetPosition to keep objects at loading position
      const position = isPreloading ? [0, -65, -150] : (offsetPosition || phoneboxConfig.position);
      gltf.scene.position.set(...position);
      
      // During preload, use neutral rotation (0) so phonebox faces camera for better material compilation
      // After preload, use the configured rotation
      const rotation = isPreloading ? [0, 0, 0] : phoneboxConfig.rotation;
      gltf.scene.rotation.set(...rotation);
      
        
      // Defer heavy mesh processing to prevent blocking the main thread
      const processMeshes = () => {
        gltf.scene.traverse((child) => {
          if (child.isMesh) {
            try {
              // Ensure material properties are set and force initialization
              if (child.material) {
                // Handle both single materials and arrays
                const materials = Array.isArray(child.material) ? child.material : [child.material];
                
                materials.forEach((material) => {
                  if (material) {
                    // Force material update to trigger shader compilation
                    material.needsUpdate = true;
                    material.transparent = false;
                    material.opacity = 1.0;
                    
                    // Force texture initialization if textures exist
                    if (material.map) {
                      material.map.needsUpdate = true;
                      // Access properties that trigger texture loading
                      void material.map.image;
                    }
                    if (material.normalMap) {
                      material.normalMap.needsUpdate = true;
                    }
                    if (material.roughnessMap) {
                      material.roughnessMap.needsUpdate = true;
                    }
                    if (material.metalnessMap) {
                      material.metalnessMap.needsUpdate = true;
                    }
                    if (material.aoMap) {
                      material.aoMap.needsUpdate = true;
                    }
                    if (material.emissiveMap) {
                      material.emissiveMap.needsUpdate = true;
                    }
                    
                    // Force shader compilation by accessing program property
                    // This will trigger onFirstUse if not already compiled
                    // We do this during preload so it's ready when user views the phone
                    if (material.isMeshStandardMaterial || material.isMeshPhysicalMaterial) {
                      // Access properties that trigger shader compilation
                      void material.type;
                      void material.vertexColors;
                    }
                  }
                });
              }
              
              // Enable shadow casting and receiving (will be controlled by showShadows setting)
              child.castShadow = true;
              child.receiveShadow = true;
              
              // Add click handlers for debug
              child.userData.originalName = child.name;
              child.userData.originalPosition = child.position.clone();
              child.userData.isClickable = true;
            } catch (error) {
              console.warn('Error processing mesh:', child.name, error);
            }
          }
        });
      };
      
      // Defer mesh processing to prevent blocking the main thread
      setTimeout(processMeshes, 0);
      
      // Set up animation mixer if animations exist
      if (gltf.animations && gltf.animations.length > 0) {
        try {
          // Validate animations before creating mixer
          const validAnimations = gltf.animations.filter(anim => 
            anim && anim.tracks && anim.tracks.length > 0
          );
          
          if (validAnimations.length > 0) {
            mixerRef.current = new THREE.AnimationMixer(gltf.scene);
            setAnimations(validAnimations);
          } else {
            console.warn('No valid animations found, skipping mixer creation');
            mixerRef.current = null;
          }
        } catch (error) {
          console.warn('Error setting up animation mixer:', error);
          mixerRef.current = null;
        }
        
        // Log available animations and their tracks
      } else {
      }
      
      // Register scene in global context for reuse
      registerSceneObject('phonebox-scene', gltf.scene);
      
      // Set reference for current use
      setSceneObject(gltf.scene);
      
      setIsModelProcessed(true);
      
      // Don't signal loaded yet - wait for readyToPosition signal
    }
  }, [gltf, registerSceneObject, getSceneObject, isModelProcessed, isPreloading, setModelLoaded, offsetPosition]);

  // Force material and shadow initialization during preload to prevent onFirstUse warnings
  // This ensures shaders are compiled before the user views the phone
  useFrame((state, delta) => {
    if (!sceneObject || materialsInitializedRef.current || !isPreloading) {
      return;
    }

    // Force shader compilation by using the renderer's compile method
    // This ensures materials and shadows are initialized during preload, not on first view
    try {
      // Access the renderer from the frame state
      const renderer = state.gl;
      if (renderer && renderer.compile) {
        // Compile the scene to force shader initialization (including shadow shaders)
        // This will trigger onFirstUse during preload instead of when user views phone
        renderer.compile(sceneObject, state.camera, state.scene);
        
        // Also ensure shadow maps are initialized if shadows are enabled
        if (performanceSettings.enableShadows) {
          // Force shadow map initialization by accessing shadow properties
          sceneObject.traverse((child) => {
            if (child.isMesh && child.castShadow) {
              // Access shadow properties to trigger initialization
              void child.castShadow;
              void child.receiveShadow;
            }
          });
          
          // Force directional light shadow map initialization
          state.scene.traverse((child) => {
            if (child.isDirectionalLight && child.castShadow) {
              // Access shadow properties to trigger map creation
              void child.shadow.map;
              void child.shadow.camera;
            }
          });
        }
        
        materialsInitializedRef.current = true;
        logger.scene('✅ Phonebox materials and shadows compiled during preload');
      }
    } catch (error) {
      // Fallback: if compile fails, materials will initialize on first render
      // This is acceptable but may cause a brief onFirstUse warning
      logger.debug('Could not force compile phonebox materials:', error);
      materialsInitializedRef.current = true; // Mark as attempted to avoid retries
    }
  });

  // Move to final position when preloader signals ready
  useEffect(() => {
    if (sceneObject && readyToPosition) {
      const phoneboxConfig = sceneConfig.modelPositions.phonebox;
      const targetPos = new THREE.Vector3(...(offsetPosition || phoneboxConfig.position));
      const targetRot = new THREE.Euler(...phoneboxConfig.rotation);
      sceneObject.position.copy(targetPos);
      sceneObject.rotation.copy(targetRot);
      
      // Signal that phonebox model is loaded and positioned (this also resolves the promise)
      setModelLoaded('phonebox', true);
    }
  }, [sceneObject, readyToPosition, setModelLoaded, offsetPosition]);

  // Update position when offset changes (after initial load)
  // Don't update during preload - keep objects at loading position
  useEffect(() => {
    if (sceneObject && readyToPosition && offsetPosition && !isPreloading) {
      const targetPos = new THREE.Vector3(...offsetPosition);
      sceneObject.position.copy(targetPos);
    }
  }, [sceneObject, readyToPosition, offsetPosition, isPreloading]);

  // Show loading state after ALL hooks
  if (isLoading) {
    return null; // Don't render anything while loading
  }

  if (error) {
    console.error('Error loading phonebox scene model:', error);
    return null;
  }
  
  return (
    <>
      {/* Render the complete phonebox scene */}
      {sceneObject && (
        <primitive 
          ref={sceneRef} 
          object={sceneObject}
          onPointerOver={(event) => {
            // Use helper function to check if the object is interactive
            if (isPhoneboxInteractiveElement(event.object) || onSceneClick) {
              setCanvasPointer(true);
              event.stopPropagation();
            }
          }}
          onPointerOut={(event) => {
            if (isPhoneboxInteractiveElement(event.object) || onSceneClick) {
              setCanvasPointer(false);
            }
          }}
          onClick={(event) => {
            if (onSceneClick) {
              onSceneClick();
            }
          }}
        />
      )}
      
      {/* Phone cable - rendered directly in PhoneboxScene */}
      {sceneObject && (
        <PhoneCable 
          phoneboxScene={sceneObject}
          visible={true}
        />
      )}
    </>
  );
}, (prevProps, nextProps) => {
  // Only re-render if the callback functions or offset position changed
  return prevProps.onSceneClick === nextProps.onSceneClick && 
         prevProps.onAnimationComplete === nextProps.onAnimationComplete &&
         JSON.stringify(prevProps.offsetPosition) === JSON.stringify(nextProps.offsetPosition);
});

const CombinedScene = React.memo(({ onSceneClick, onAnimationComplete }) => {
  const { activeScreen } = useActiveScreen();
  const { cameraRef, phoneboxState, setPhoneOffHook, setIsInQuestionMode, acquireQuestionSequenceLock, releaseQuestionSequenceLock, isQuestionSequenceLocked, tvState, setTvZoomed } = useCanvasContext();
  const { permissionState } = useMicrophone();
  const { performanceSettings } = useGPUTier();
  const { toggleSound } = useSound();
  const [lastLoggedRotation, setLastLoggedRotation] = useState(null);
  const [phoneAudioEnded, setPhoneAudioEnded] = useState(false);
  const breakpoint = useBreakpoint();
  const questionSequenceLockRef = useRef(null);
  const previousScreenRef = useRef(activeScreen);
  const rotationDirectionRef = useRef(0); // Track rotation direction: 1 for forward, -1 for backward
  
  // Track screen changes to detect wrapping direction
  // Screen order matches rotation angles: logo (0°) -> phone (90°) -> news (180°) -> tv (270°) -> logo (360°/0°)
  useEffect(() => {
    if (previousScreenRef.current !== activeScreen) {
      const screenOrder = [null, 'phone', 'news', 'tv'];
      const prevIndex = screenOrder.indexOf(previousScreenRef.current);
      const currentIndex = screenOrder.indexOf(activeScreen);
      
      if (prevIndex !== -1 && currentIndex !== -1) {
        // Determine direction based on screen order
        // Forward: moving to higher index (or wrapping from last to first)
        // Backward: moving to lower index (or wrapping from first to last)
        if (prevIndex === screenOrder.length - 1 && currentIndex === 0) {
          // Wrapping forward: tv -> logo
          rotationDirectionRef.current = 1;
        } else if (prevIndex === 0 && currentIndex === screenOrder.length - 1) {
          // Wrapping backward: logo -> tv
          rotationDirectionRef.current = -1;
        } else if (currentIndex > prevIndex) {
          // Forward movement
          rotationDirectionRef.current = 1;
        } else if (currentIndex < prevIndex) {
          // Backward movement
          rotationDirectionRef.current = -1;
        }
      } else if (prevIndex === -1 && currentIndex !== -1) {
        // Starting from logo (null), determine direction based on which screen we're going to
        if (currentIndex === screenOrder.length - 1) {
          // Going to tv from logo (backward)
          rotationDirectionRef.current = -1;
        } else {
          // Going forward from logo
          rotationDirectionRef.current = 1;
        }
      }
      previousScreenRef.current = activeScreen;
    }
  }, [activeScreen]);
  
  // Clear responsive caches when breakpoint changes to ensure positions recalculate
  useEffect(() => {
    clearResponsiveCaches();
  }, [breakpoint]);

  // Create optimized scene configuration based on GPU tier
  const optimizedSceneConfig = useMemo(() => 
    createOptimizedSceneConfig(performanceSettings), 
    [performanceSettings]
  );

  // Memoize logo offset position - recalculate when breakpoint changes
  const logoOffsetPosition = useMemo(() => {
    return applySceneOffset('logo');
  }, []);

  // Memoize phonebox offset position - recalculate when breakpoint changes
  const phoneboxOffsetPosition = useMemo(() => {
    return applySceneOffset('phonebox');
  }, []);

  // Memoize TV offset position - recalculate when breakpoint changes
  const tvOffsetPosition = useMemo(() => {
    return applySceneOffset('tv');
  }, []);

  // Memoize news offset position - recalculate when breakpoint changes
  const newsOffsetPosition = useMemo(() => {
    return applySceneOffset('news');
  }, []);

  // Use ref to access current phoneboxState without making it a dependency
  const phoneboxStateRef = useRef(phoneboxState);
  phoneboxStateRef.current = phoneboxState;

  const handlePhoneboxClick = useCallback(async () => {
    // This handler is called when clicking on the phonebox scene
    // If phone is off-hook, delegate to parent to handle hangup (no debounce)
    if (phoneboxStateRef.current.isOffHook) {
      // Phone is off-hook - let parent handle hangup immediately
      if (onSceneClick) {
        onSceneClick();
      }
      return;
    }
    
    // Phone is on-hook - handle pickup
    // Only allow pickup if phone is fully on hook (not transitioning)
    const isFullyOnHook = !phoneboxStateRef.current.isOffHook && phoneboxStateRef.current.animationState === 'idle';
    
    // If trying to pick up but phone is not fully on hook, ignore the click
    if (!isFullyOnHook) {
      logger.debug('Phone not fully on hook - ignoring pickup attempt', {
        isOffHook: phoneboxStateRef.current.isOffHook,
        animationState: phoneboxStateRef.current.animationState
      });
      return;
    }
    
    // Ensure audio context is resumed before handling the click
    if (phoneboxStateRef.current.audioContext && phoneboxStateRef.current.audioContext.state === 'suspended') {
      try {
        await phoneboxStateRef.current.audioContext.resume();
      } catch (error) {
        console.warn('Could not resume audio context for phonebox click:', error);
      }
    }
    
    // Trigger the phonebox animation by setting off-hook state (pickup)
    setPhoneOffHook(true, { source: 'combined_scene', reason: 'pickup' });
    
    if (onSceneClick) {
      onSceneClick();
    }
  }, [onSceneClick, setPhoneOffHook]);

  const handleTvClick = useCallback(() => {
    // Toggle TV zoom state
    const nextState = !tvState.isZoomed;
    setTvZoomed(nextState, {
      source: 'tv_click',
      reason: nextState ? 'zoom_in' : 'zoom_out'
    });
  }, [tvState.isZoomed, setTvZoomed]);

  const handleAnimationComplete = useCallback(() => {
    if (onAnimationComplete) {
      onAnimationComplete();
    }
  }, [onAnimationComplete]);

  const handleAudioEnd = useCallback(() => {
    // This will be called by PhoneboxScene when audio ends
    logger.debug('CombinedScene: Phone audio ended, checking microphone permission...');
    logger.debug('CombinedScene: Current microphone permission state:', permissionState);
    
    // Mark that phone audio has ended
    setPhoneAudioEnded(true);
    
    // Only start question sequence if microphone permission is granted and no sequence is already active
    if (permissionState === 'granted' && !isQuestionSequenceLocked()) {
      logger.debug('CombinedScene: Microphone permission granted and no active sequence - starting question sequence');
      const lockId = acquireQuestionSequenceLock();
      if (lockId) {
        questionSequenceLockRef.current = lockId;
        setIsInQuestionMode(true, { source: 'combined_scene' });
      } else {
        logger.warn('CombinedScene: Failed to acquire question sequence lock');
      }
    } else if (isQuestionSequenceLocked()) {
      logger.debug('CombinedScene: Question sequence already active, skipping start');
    } else {
      logger.debug('CombinedScene: Microphone permission not granted - waiting for permission');
      // The MicrophonePermissionUI will handle showing the permission request
      // Once permission is granted, the useEffect will start the question sequence
    }
  }, [setIsInQuestionMode, permissionState, acquireQuestionSequenceLock, isQuestionSequenceLocked]);

  // Listen for microphone permission changes to start question sequence if needed
  useEffect(() => {
    // Only start question sequence if:
    // 1. Phone is off-hook (user picked up phone)
    // 2. Phone audio has ended (we're past the initial phone audio)
    // 3. Microphone permission is granted
    // 4. We're not already in question mode
    // 5. No question sequence is already active
    if (phoneboxState.isOffHook && 
        phoneAudioEnded &&
        permissionState === 'granted' && 
        !phoneboxState.isInQuestionMode &&
        !isQuestionSequenceLocked()) {
      logger.debug('CombinedScene: Both phone audio ended and microphone permission granted - starting question sequence');
      const lockId = acquireQuestionSequenceLock();
      if (lockId) {
        questionSequenceLockRef.current = lockId;
        setIsInQuestionMode(true, { source: 'combined_scene' });
      } else {
        logger.warn('CombinedScene: Failed to acquire question sequence lock in useEffect');
      }
    } else if (isQuestionSequenceLocked()) {
      logger.debug('CombinedScene: Question sequence already active, skipping start in useEffect');
    }
  }, [permissionState, phoneboxState.isOffHook, phoneAudioEnded, phoneboxState.isInQuestionMode, setIsInQuestionMode, acquireQuestionSequenceLock, isQuestionSequenceLocked]);

  // Cleanup question sequence lock when phone is hung up
  useEffect(() => {
    if (!phoneboxState.isOffHook && questionSequenceLockRef.current) {
      logger.debug('CombinedScene: Phone hung up - releasing question sequence lock');
      releaseQuestionSequenceLock(questionSequenceLockRef.current);
      questionSequenceLockRef.current = null;
      setPhoneAudioEnded(false); // Reset phone audio ended state
    }
  }, [phoneboxState.isOffHook, releaseQuestionSequenceLock]);

  // Unified camera rotation and position handling for all screens
  useConditionalFrame(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (cameraRef?.current && cameraRef.current.rotation && typeof cameraRef.current.rotation.y === 'number') {
      // Get camera state configuration based on active screen
      let cameraState;
      if (activeScreen === 'phone') {
        cameraState = sceneConfig.cameraStates.phonebox;
      } else if (activeScreen === 'tv' || activeScreen === 'news') {
        cameraState = sceneConfig.cameraStates[activeScreen];
      } else {
        // Logo state (null) or any other state
        cameraState = sceneConfig.cameraStates.logo;
      }
      
      const targetRotation = cameraState.rotation[1];
      const targetPosition = cameraState.position;
      const currentRotation = cameraRef.current.rotation.y;
      const currentPos = cameraRef.current.position;
      const targetPos = new THREE.Vector3(...targetPosition);
      const positionDiff = targetPos.distanceTo(currentPos);
      
      // Screen order: logo (0°) -> phone (90°) -> news (180°) -> tv (270°) -> logo (360°/0°)
      const screenOrder = [null, 'phone', 'news', 'tv'];
      const prevIndex = screenOrder.indexOf(previousScreenRef.current);
      const currentIndex = screenOrder.indexOf(activeScreen);
      
      // Detect wrapping: going from last screen (tv) to first (logo/null) or vice versa
      // Also handle case where previousScreen is null (logo) and we're going to tv
      const isWrapping = 
        (prevIndex === screenOrder.length - 1 && (currentIndex === 0 || currentIndex === -1)) || 
        ((prevIndex === 0 || prevIndex === -1) && currentIndex === screenOrder.length - 1);
      
      // Helper function to normalize angles to 0-2π range
      const normalizeAngle = (angle) => {
        while (angle < 0) angle += Math.PI * 2;
        while (angle >= Math.PI * 2) angle -= Math.PI * 2;
        return angle;
      };
      
      // Calculate rotation difference with wrapping support
      let rotationDiff;
      const currentNorm = normalizeAngle(currentRotation);
      const targetNorm = normalizeAngle(targetRotation);
      let diff = targetNorm - currentNorm;
      
      if (isWrapping) {
        // When wrapping, force rotation in the tracked direction
        // If direction isn't set, infer it from the screen indices
        let direction = rotationDirectionRef.current;
        if (direction === 0) {
          // Infer direction from screen transition
          if (prevIndex === screenOrder.length - 1 && (currentIndex === 0 || currentIndex === -1)) {
            direction = 1; // Forward: tv -> logo
          } else if ((prevIndex === 0 || prevIndex === -1) && currentIndex === screenOrder.length - 1) {
            direction = -1; // Backward: logo -> tv
          }
        }
        
        if (direction > 0) {
          // Forward wrap: ensure positive rotation (270° -> 360°/0°)
          if (diff <= 0) {
            diff += Math.PI * 2;
          }
        } else if (direction < 0) {
          // Backward wrap: ensure negative rotation (0° -> -90°/270°)
          if (diff >= 0) {
            diff -= Math.PI * 2;
          }
        }
        rotationDiff = diff;
      } else {
        // Normal rotation: use shortest path
        if (Math.abs(diff) > Math.PI) {
          diff = diff > 0 ? diff - Math.PI * 2 : diff + Math.PI * 2;
        }
        rotationDiff = diff;
      }
      
      // Apply rotation interpolation
      const rotationSpeed = 0.12;
      const rotationThreshold = 0.01;
      
      if (Math.abs(rotationDiff) > rotationThreshold) {
        cameraRef.current.rotation.y += rotationDiff * rotationSpeed;
      } else {
        // Snap to target when close
        const finalRotation = isWrapping ? normalizeAngle(targetRotation) : targetRotation;
        cameraRef.current.rotation.y = finalRotation;
        if (lastLoggedRotation !== finalRotation) {
          setLastLoggedRotation(finalRotation);
        }
      }
      
      // Apply position interpolation (skip for phone and tv states - handled by zoom loops below)
      if (activeScreen !== 'phone' && activeScreen !== 'tv') {
        const positionSpeed = 0.12;
        if (positionDiff > 0.01) {
          currentPos.lerp(targetPos, positionSpeed);
        } else {
          cameraRef.current.position.copy(targetPos);
        }
      }
    }
    
    // Smooth camera interpolation with zoom-in for phonebox interaction
    // Only apply this logic when we're in the phone state
    if (cameraRef?.current && cameraRef.current.position && activeScreen === 'phone') {
      const currentPos = cameraRef.current.position;
      
      // Determine target position based on phonebox state
      let targetPosition;
      if (phoneboxState.isOffHook) {
        // Use the off-hook camera position from configuration
        const offHookCameraState = sceneConfig.cameraStates.phoneboxOffHook;
        targetPosition = offHookCameraState.position;
      } else {
        // Use the phonebox camera position from configuration
        const phoneboxCameraState = sceneConfig.cameraStates.phonebox;
        targetPosition = phoneboxCameraState.position;
      }
      
      // Camera stays at original position - scenes move instead
      const targetPos = new THREE.Vector3(...targetPosition);
      
      // Check if we need to move the camera
      const distance = currentPos.distanceTo(targetPos);
      
      // Only move if we're not already at the target position
      if (distance > 0.5) { // Much larger threshold to prevent infinite zooming
        // Faster interpolation for phone zoom movement to reach target quicker
        currentPos.lerp(targetPos, 0.15); // Much faster speed for quicker convergence
      } else if (distance > 0.1) {
        // When getting close, use slower interpolation
        currentPos.lerp(targetPos, 0.05);
      } else {
        // Ensure exact position when close enough
        currentPos.copy(targetPos);
      }
    }
    
    // Smooth camera interpolation with zoom-in for TV interaction
    // Only apply this logic when we're in the TV state
    if (cameraRef?.current && cameraRef.current.position && activeScreen === 'tv') {
      const currentPos = cameraRef.current.position;
      
      // Determine target position based on TV zoom state
      let targetPosition;
      if (tvState.isZoomed) {
        // Use the zoomed camera position from configuration
        const tvZoomedCameraState = sceneConfig.cameraStates.tvZoomed;
        targetPosition = tvZoomedCameraState.position;
      } else {
        // Use the normal TV camera position from configuration
        const tvCameraState = sceneConfig.cameraStates.tv;
        targetPosition = tvCameraState.position;
      }
      
      // Camera stays at original position - scenes move instead
      const targetPos = new THREE.Vector3(...targetPosition);
      
      // Check if we need to move the camera
      const distance = currentPos.distanceTo(targetPos);
      
      // Only move if we're not already at the target position
      if (distance > 0.5) { // Much larger threshold to prevent infinite zooming
        // Faster interpolation for TV zoom movement to reach target quicker
        currentPos.lerp(targetPos, 0.15); // Much faster speed for quicker convergence
      } else if (distance > 0.1) {
        // When getting close, use slower interpolation
        currentPos.lerp(targetPos, 0.05);
      } else {
        // Ensure exact position when close enough
        currentPos.copy(targetPos);
      }
    }
  }, [activeScreen, cameraRef, lastLoggedRotation, phoneboxState.isOffHook, tvState.isZoomed], {
    maxFPS: performanceSettings.maxFPS, // Use GPU tier performance settings
    pauseWhenHidden: false, // Keep camera movement even when hidden
    reduceFPSWhenHidden: true,
    hiddenFPS: Math.max(performanceSettings.maxFPS / 2, 15) // Half of maxFPS, minimum 15
  });
  
  return (
    <>
      {/* Lighting setup for shadows and horizon effects - optimized for GPU tier */}
      <ambientLight intensity={optimizedSceneConfig.lighting.ambientIntensity} />
      <directionalLight
        position={optimizedSceneConfig.lighting.directionalPosition}
        intensity={optimizedSceneConfig.lighting.directionalIntensity}
        castShadow={optimizedSceneConfig.showShadows}
        shadowMapSizeWidth={optimizedSceneConfig.lighting.shadowMapSize}
        shadowMapSizeHeight={optimizedSceneConfig.lighting.shadowMapSize}
        shadowCameraFar={optimizedSceneConfig.lighting.shadowCamera.far}
        shadowCameraLeft={optimizedSceneConfig.lighting.shadowCamera.left}
        shadowCameraRight={optimizedSceneConfig.lighting.shadowCamera.right}
        shadowCameraTop={optimizedSceneConfig.lighting.shadowCamera.top}
        shadowCameraBottom={optimizedSceneConfig.lighting.shadowCamera.bottom}
      />
      
      {/* Dark red floor plane that matches site background - positioned at phonebox bottom level */}
      {optimizedSceneConfig.showFloor && (
        <mesh 
          position={optimizedSceneConfig.floor.position} 
          rotation={[-Math.PI / 2, 0, 0]}
          receiveShadow={optimizedSceneConfig.showShadows}
        >
          <planeGeometry args={optimizedSceneConfig.floor.size} />
          <meshLambertMaterial color={optimizedSceneConfig.floor.color} />
        </mesh>
      )}
      
      
      {/* Always render AnimatedLogos (animated logos) with responsive offset */}
      <AnimatedLogos offsetPosition={logoOffsetPosition} />
      
      {/* Always render all 3D models - they should be visible from the start */}
      <Phonebox3DScene 
        onSceneClick={handlePhoneboxClick} 
        onAnimationComplete={handleAnimationComplete}
        onAudioEnd={handleAudioEnd}
        offsetPosition={phoneboxOffsetPosition}
      />
      
      <TvScene 
        onSceneClick={handleTvClick}
        onButton1Click={() => {
          // Button click is now handled by setTvChannel in TvScene
          // This callback is kept for compatibility but not used
        }}
        onButton2Click={() => {
          // Toggle sound on/off
          toggleSound();
        }}
        offsetPosition={tvOffsetPosition}
      />
      
      <NewsScene 
        onSceneClick={() => {
          // News scene clicks should not trigger phonebox animation
        }}
        offsetPosition={newsOffsetPosition}
      />
      
      {/* Phonebox labels aligned with camera when on phone screen */}
      {activeScreen === 'phone' && (
        <CameraAlignedLabels
          visible={!phoneboxState.isOffHook}
          sceneName="phonebox-scene"
          leftChildren="ANSWER THE PHONE"
        />
      )}
      
      {/* TV labels aligned with camera when on TV screen */}
      {activeScreen === 'tv' && (
        <CameraAlignedLabels
          visible={true}
          sceneName="tv-scene"
          leftChildren="WATCH THE TV"
        />
      )}
      
      {/* SoundWaves rendered inside Canvas relative to phone */}
      {/* Temporarily render always for debugging */}
      {activeScreen === 'phone' && <SoundWaves />}
      {/* Original: {activeScreen === 'phone' && !isCorporateMode && <SoundWaves />} */}
    </>
  );
}, (prevProps, nextProps) => {
  // Only re-render if the callback functions actually changed
  return prevProps.onSceneClick === nextProps.onSceneClick && 
         prevProps.onAnimationComplete === nextProps.onAnimationComplete;
});

export default CombinedScene;
